import * as React from "react";
import { Navigator } from "../../../../../../Shared/Wizards/Entities/Navigator";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { Urls } from "../../../../../Urls";
import { adviserParse, IReviewProps, ongoingAdviceParse, prepareTypography, regularAdviceParse } from "../ReviewParser";
import { NewQuoteAction } from "../../../NewQuoteWizard/Actions/NewQuoteAction";
import { ChargeFromMap } from "../../AdviserCharges/Resource/Contents";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { FrequencyArraysMap, SourceTypeMap } from "../../Resource/Contents";
import { MoneyOrPercentageType } from "../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { PensionOption } from "../../../../../../../Wrap/Constants/Enums/Apply/PensionOption";
import { PensionContribution } from "../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContribution";
import { IsaContribution } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Isa/IsaContribution";
import { GiaContribution } from "../../../../../../../Wrap/Models/Models/Processes/Apply/PersonalPortfolio/GiaContribution";
import { WrapCashContribution } from "../../../../../../../Wrap/Models/Models/Processes/Apply/WrapCash/WrapCashContribution";

interface IProps extends IReviewProps{
    data: PensionContribution | GiaContribution | IsaContribution | WrapCashContribution
}
export const AdviceChargeReview = (props: IProps) => {
    const { data, subAccountType, process } = props;
    const checkNoneCharges = (config) => {
        return typeof config.data != "boolean";
    };
    const adviceCahrgeConfig = () => {
        if (!data) {return [];}
        const config = [
            {
                title: "Initial adviser charge",
                data: adviserParse(data.InitialAdviserCharge),
            },
            {
                title: "Regular payment adviser charge",
                data: regularAdviceParse(data.RegularPaymentInitialAdviceCharge),
            },
            {
                title: "Ongoing adviser charge",
                data: ongoingAdviceParse(data.OngoingAdviceCharge),
            },
        ];
        return config.filter(checkNoneCharges);
    };

    const navigate = () => {
        if (process == ProcessType.NB) {
            Navigator.navigateToAndPreserveQueryParameters(adviceChargeUrlConfig());
        } else if (process == ProcessType.Quote) {
            adviceChargeUrlConfigForNewQuote();
        } else if (process == ProcessType.SinglePayment) {
            Navigator.navigateToAndPreserveQueryParameters(Urls.get().SPTAdviserCharge);
        } else if (process == ProcessType.AddRegular || ProcessType.EditRegular) {
            Navigator.navigateToAndPreserveQueryParameters(Urls.get().RegularPaymentAdviserCharge);
        }
    };

    const adviceChargeUrlConfigForNewQuote = () => {
        switch (subAccountType) {
            case SubAccountType.InvestmentAccount:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuoteAdviserChargesPP);
            case SubAccountType.StocksIsa:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuoteAdviserChargesISA);
            case SubAccountType.Pension:
            case SubAccountType.Sipp:
            case SubAccountType.Npr:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuoteAdviserChargesSIPP);
            case SubAccountType.JuniorSipp:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuoteAdviserChargesJSIPP);
            default:
                break;
        }
    };

    const adviceChargeUrlConfig = () => {
        switch (subAccountType) {
            case SubAccountType.StocksIsa:
                return Urls.get().NBAdviserChargesISA;
            case SubAccountType.InvestmentAccount:
                return Urls.get().NBAdviserChargesPP;
            case SubAccountType.Npr:
                return Urls.get().NBAdviserChargesSIPP;
            case SubAccountType.JuniorSipp:
                return Urls.get().NBAdviserChargesJSIPP;
            default:
                break;
        }
    };

    const adviserChargesConfig = () => {
        if (!data) return [];
        const DrawdownModel = (data as PensionContribution)?.DrawdownModel;
        const adviserCharges = [];
        adviserCharges.push({
            title: <strong>Adviser charges</strong>,
            data: " ",
        });
        if (
            DrawdownModel?.DrawdownType != DrawdownType.Regular &&
            DrawdownModel?.PensionOption != PensionOption.UFPLS
        ) {
            adviserCharges.push({
                title: "Rate",
                data:
                    DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Type ===
                    MoneyOrPercentageType.Money
                        ? MoneyDisplayHelper.getMoneyFormat(
                              DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Money
                          )
                        : DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Percentage + "%",
            });
            adviserCharges.push({
                title: "Take the charge from - DIAC",
                data: SourceTypeMap[DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.SourceType],
            });
        }
        adviserCharges.push({
            title: "Annual rate",
            data: DrawdownModel?.DrawdownAdviserCharge?.OngoingAdviserCharge
                ? DrawdownModel?.DrawdownAdviserCharge?.OngoingAdviserCharge?.Type == MoneyOrPercentageType.Money
                    ? MoneyDisplayHelper.getMoneyFormat(
                          DrawdownModel?.DrawdownAdviserCharge?.OngoingAdviserCharge?.Money
                      )
                    : DrawdownModel?.DrawdownAdviserCharge?.OngoingAdviserCharge?.Percentage + "%"
                : null,
        });
        adviserCharges.push({
            title: "Deduction frequency",
            data: FrequencyArraysMap[DrawdownModel?.DrawdownAdviserCharge?.OngoingAdviserCharge?.Frequency],
        });
        adviserCharges.push({
            title: "Take the charge from - OAC",
            data: ChargeFromMap[DrawdownModel?.DrawdownAdviserCharge?.OngoingAdviserCharge?.SourceType],
        });
        return adviserCharges;
    };

    return (
        <>
            {process == ProcessType.TopUpWithITFLS ? (
                <Accordion
                    label="Adviser charges"
                    isOpen={false}
                    onEdit={() => (props.onEditAction ? props.onEditAction() : navigate())}
                >
                    <div className="pl-34">
                        <Typography {...prepareTypography(adviserChargesConfig(), 1, "common")}></Typography>
                    </div>
                </Accordion>
            ) : (
                <Accordion
                    label="Adviser charges"
                    isOpen={false}
                    onEdit={() => (props.onEditAction ? props.onEditAction() : navigate())}
                >
                    <div className="pl-34">
                        <Typography type="common" config={adviceCahrgeConfig()}></Typography>
                    </div>
                </Accordion>
            )}
        </>
    );
};
